import { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import { SERVICES } from "@/lib/content";

const SIZES = ["Small (< 200m²)", "Medium (200–600m²)", "Large (600m²+)"];
const FREQ = ["One-off", "Weekly", "Fortnightly", "Monthly"];

export const QuoteDialog = ({ open, onOpenChange, defaultService }) => {
  const [services, setServices] = useState([defaultService || SERVICES[0].id]);
  const [size, setSize] = useState(SIZES[1]);
  const [freq, setFreq] = useState(FREQ[2]);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open && defaultService) setServices([defaultService]);
  }, [open, defaultService]);

  useEffect(() => {
    const lenis = typeof window !== "undefined" ? window.__lenis : null;
    if (!lenis) return;
    if (open) lenis.stop();
    else lenis.start();
    return () => lenis.start();
  }, [open]);

  const toggleService = (id) => {
    setServices((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name || !phone || !email) {
      toast.error("Please add your name, phone and email.");
      return;
    }
    if (services.length === 0) {
      toast.error("Please select at least one service.");
      return;
    }
    setSending(true);
    try {
      const serviceTitles = SERVICES.filter((s) => services.includes(s.id)).map((s) => s.title).join(", ");
      const res = await axios.post("https://api.web3forms.com/submit", {
        access_key: process.env.REACT_APP_WEB3FORMS_KEY,
        subject: `New quote request from ${name}`,
        from_name: "CBA Mowing Website",
        name,
        phone,
        email,
        address: address || "—",
        services: serviceTitles,
        approximate_lawn_size: size,
        frequency: freq,
        notes: notes || "—",
      }, { headers: { "Content-Type": "application/json", Accept: "application/json" } });
      if (!res.data.success) throw new Error(res.data.message);
      toast.success("Quote request sent!", {
        description: "The CBA Mowing team will be in touch within one business day.",
      });
      onOpenChange(false);
      setName(""); setPhone(""); setEmail(""); setAddress(""); setNotes("");
    } catch (err) {
      toast.error("Couldn't send your request.", {
        description: "Please try again, or call us directly.",
      });
    } finally {
      setSending(false);
    }
  };

  const Chip = ({ active, children, onClick, testId }) => (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`rounded-full px-4 py-2 text-sm font-medium border transition-colors duration-200 ${
        active ? "bg-[#0B0F0C] text-white border-[#0B0F0C]" : "bg-white text-[#52635A] border-black/10 hover:border-[#05C46B]"
      }`}
    >
      {children}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="quote-dialog" data-lenis-prevent className="max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold tracking-tight">Get your free estimate</DialogTitle>
          <p className="text-sm text-[#52635A]">Tell us about your space — no obligation.</p>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-6 pt-2">
          <div>
            <p className="font-mono-t text-[11px] uppercase tracking-widest text-[#52635A] mb-3">Services — pick as many as you need</p>
            <div className="flex flex-wrap gap-2">
              {SERVICES.map((s) => (
                <Chip key={s.id} testId={`quote-service-${s.id}`} active={services.includes(s.id)} onClick={() => toggleService(s.id)}>
                  {s.title.split(" & ")[0]}
                </Chip>
              ))}
            </div>
          </div>

          <div>
            <p className="font-mono-t text-[11px] uppercase tracking-widest text-[#52635A] mb-3">Approximate lawn size</p>
            <div className="flex flex-wrap gap-2">
              {SIZES.map((s) => (
                <Chip key={s} testId={`quote-size-${s.split(" ")[0].toLowerCase()}`} active={size === s} onClick={() => setSize(s)}>{s}</Chip>
              ))}
            </div>
          </div>

          <div>
            <p className="font-mono-t text-[11px] uppercase tracking-widest text-[#52635A] mb-3">Frequency</p>
            <div className="flex flex-wrap gap-2">
              {FREQ.map((f) => (
                <Chip key={f} testId={`quote-freq-${f.toLowerCase()}`} active={freq === f} onClick={() => setFreq(f)}>{f}</Chip>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3">
            <Input data-testid="quote-name" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} className="rounded-xl h-12" />
            <Input data-testid="quote-phone" type="tel" placeholder="Phone number" value={phone} onChange={(e) => setPhone(e.target.value)} className="rounded-xl h-12" />
            <Input data-testid="quote-email" type="email" placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-xl h-12" />
            <Input data-testid="quote-address" placeholder="Property address (house number, street, suburb)" value={address} onChange={(e) => setAddress(e.target.value)} className="rounded-xl h-12" />
            <Textarea data-testid="quote-notes" placeholder="Anything we should know? (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} className="rounded-xl min-h-[80px]" />
          </div>

          <button
            type="submit"
            disabled={sending}
            data-testid="quote-form-submit-button"
            className="w-full rounded-full bg-[#00E676] text-[#0B0F0C] font-semibold py-3.5 hover:bg-[#05C46B] transition-colors duration-300 flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {sending ? "Sending…" : "Send Quote Request"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
